import { useState, useEffect, useRef } from "react";
import { 
    calculateFinancialSummary, 
    calculateAnnualFinancialSummary, 
    formatCurrency,
    formatDate,
    isNegative,
    getVal 
} from "../../helpers/process_morningstar.js";
import { fetchStockData } from "../../helpers/graph_middleware.js"
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  CircularProgress,
} from "@mui/material";


export default function ProgVargs() {
  const tableRef = useRef(null);
  const [tables, setTables] = useState([]);
  const [error, setError] = useState(null);
  const [quarterlyTableData, setQuarterlyTableData] = useState([]);
  const [annualTableData, setAnnualTableData] = useState([]);
  const [stockTableData, setStockTableData] = useState([]);
  const [twoYearStock, setTwoYearStock] = useState([]);
  const [Tax, setTax] = useState(getVal("selectedTax"));
  const [capRD, setCapRD] = useState(getVal("capRD"));
  const [includeOtherIncome, setIncludeOtherIncome] = useState(getVal("includeOtherIncome"));
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (tableRef.current) {
      const el = tableRef.current;
      el.scrollTo({
        left: el.scrollWidth,
      });
    }
  }, [quarterlyTableData]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "capRD") setCapRD(JSON.parse(event.newValue));
      if (event.key === "includeOtherIncome") setIncludeOtherIncome(JSON.parse(event.newValue));
    };
  
    const handleCustomUpdate = () => {
      setCapRD(getVal("capRD"));
      setIncludeOtherIncome(getVal("includeOtherIncome"));
    };
  
    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("localStorageUpdated", handleCustomUpdate);
  
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("localStorageUpdated", handleCustomUpdate);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const quarterlyFinancialData = await calculateFinancialSummary(capRD, includeOtherIncome, Tax);
      setQuarterlyTableData(quarterlyFinancialData);

      const annualFinancialSummary = await calculateAnnualFinancialSummary(capRD, includeOtherIncome, Tax);
      setAnnualTableData(annualFinancialSummary)

      const stockData = await fetchStockData()
      const formattedStockData = [...stockData[0]].reverse()
      setStockTableData(formattedStockData)

      const stockDataTY = [...stockData[2]].reverse();
      setTwoYearStock(stockDataTY);
    };

    fetchData();
    setLoading(false)
  }, [capRD, includeOtherIncome, Tax]);

  if (loading) {
    return (
      <Container sx={{ textAlign: "center", mt: 4 }}>
        <CircularProgress />
        <Typography mt={2}>Loading MorningStar Data...</Typography>
      </Container>
    );
  }

  return (
    <>
        <>
        {quarterlyTableData.length > 0 ? (
            <Box sx={{ overflowX: "auto", whiteSpace: "nowrap", width: "100%" }}>
                <Box sx={{ display: "inline-flex" }} >

                {/*CFIRR*/}
                <Box>
                <TableContainer component={Paper} sx={{ 
                    overflowX: 'auto', 
                    boxShadow: "none",
                    borderRadius: 2, 
                    width: 'fit-content',              
                    maxWidth: '100%',
                    mx: 2
                    }}
                >
                <Table stickyHeader size="small">
                    {/* HEADER */}
                    <TableHead>
                    <TableRow>
                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold",
                            borderRight: "1px solid #969696ff"
                        }}
                        >
                        Annual
                        </TableCell>

                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold"
                        }}
                        >
                        CFIRR
                        </TableCell>
                    </TableRow>
                    </TableHead>

                    {/* BODY */}
                    <TableBody>
                    {annualTableData.map((row, idx) => (
                        <TableRow key={idx}>
                        
                        {/* COLUMN 2 → DATE */}
                        <TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>
                            {formatDate(row["Date"])}
                        </TableCell>

                        {/* COLUMN 1 → CFIRR */}
                        <TableCell sx={{ textAlign: "left" }}>
                            {row["CFIRRExclTFQ"]}
                        </TableCell>

                        </TableRow>
                    ))}
                    </TableBody>

                </Table>

                <Table stickyHeader size="small">

                    {/* HEADER */}
                    <TableHead>
                    <TableRow>
                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold",
                            borderRight: "1px solid #969696ff"
                        }}
                        >
                        Quarterly
                        </TableCell>

                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold"
                        }}
                        >
                        CFIRR
                        </TableCell>
                    </TableRow>
                    </TableHead>

                    {/* BODY */}
                    <TableBody>
                    {quarterlyTableData.map((row, idx) => (
                        <TableRow key={idx}>
                        
                        {/* COLUMN 2 → DATE */}
                        <TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>
                            {formatDate(row["Date"])}
                        </TableCell>

                        {/* COLUMN 1 → CFIRR */}
                        <TableCell sx={{ textAlign: "left" }}>
                            {row["CFIRRExclTFQ"]}
                        </TableCell>

                        </TableRow>
                    ))}
                    </TableBody>

                </Table>
                </TableContainer>
                </Box>

                {/*Capital Grw & Plowback */}
                <Box>
                <TableContainer component={Paper} sx={{
                    overflowX: 'auto', 
                    boxShadow: "none",
                    borderRadius: 2, 
                    width: 'fit-content',              
                    maxWidth: '100%',
                    mx: 2
                    }}
                >
                <Table stickyHeader size="small">

                    {/* HEADER */}
                    <TableHead>
                    <TableRow>
                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold",
                            borderRight: "1px solid #969696ff"
                        }}
                        >
                        Annual
                        </TableCell>

                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold"
                        }}
                        >
                        Capital Grw
                        </TableCell>
                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold"
                        }}
                        >
                        Plowback
                        </TableCell>
                    </TableRow>
                    </TableHead>

                    {/* BODY */}
                    <TableBody>
                    {annualTableData.map((row, idx) => (
                        <TableRow key={idx}>
                        
                        <TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>
                            {formatDate(row["Date"])}
                        </TableCell>

                        <TableCell sx={{ textAlign: "left" }}>
                            {row["InflAdjGrossInvestedCapitalExclGrw"]}
                        </TableCell>

                        <TableCell sx={{ textAlign: "left" }}>
                            {row["plowback"]}
                        </TableCell>

                        </TableRow>
                    ))}
                    </TableBody>

                </Table>

                <Table stickyHeader size="small">

                    {/* HEADER */}
                    <TableHead>
                    <TableRow>
                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold",
                            borderRight: "1px solid #969696ff"
                        }}
                        >
                        Quarterly
                        </TableCell>

                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold"
                        }}
                        >
                        Capital Grw
                        </TableCell>

                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold"
                        }}
                        >
                        Plowback
                        </TableCell>
                    </TableRow>
                    </TableHead>

                    {/* BODY */}
                    <TableBody>
                    {quarterlyTableData.map((row, idx) => (
                        <TableRow key={idx}>
                        
                        <TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>
                            {formatDate(row["Date"])}
                        </TableCell>

                        <TableCell sx={{ textAlign: "left" }}>
                            {row["InflAdjGrossInvestedCapitalExclGrw"]}
                        </TableCell>

                        <TableCell sx={{ textAlign: "left" }}>
                            {row["plowback"]}
                        </TableCell>

                        </TableRow>
                    ))}
                    </TableBody>

                </Table>
                </TableContainer>
                </Box>

                {/* EPS */}
                <Box>
                <TableContainer component={Paper} sx={{ 
                    overflowX: 'auto', 
                    boxShadow: "none",
                    borderRadius: 2, 
                    width: 'fit-content',              
                    maxWidth: '100%',
                    mx: 2
                    }}
                >
                <Table stickyHeader size="small">
                    {/* HEADER */}
                    <TableHead>
                    <TableRow>
                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold",
                            borderRight: "1px solid #969696ff"
                        }}
                        >
                        Annual
                        </TableCell>

                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold"
                        }}
                        >
                        ESP
                        </TableCell>
                    </TableRow>
                    </TableHead>

                    {/* BODY */}
                    <TableBody>
                    {annualTableData.map((row, idx) => (
                        <TableRow key={idx}>
                        
                        {/* COLUMN 2 → DATE */}
                        <TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>
                            {formatDate(row["Date"])}
                        </TableCell>

                        {/* COLUMN 1 → CFIRR */}
                        <TableCell sx={{ textAlign: "left" }}>
                            {row["normalizedBasicEps"]}
                        </TableCell>

                        </TableRow>
                    ))}
                    </TableBody>

                </Table>

                <Table stickyHeader size="small">

                    {/* HEADER */}
                    <TableHead>
                    <TableRow>
                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold",
                            borderRight: "1px solid #969696ff"
                        }}
                        >
                        Quarterly
                        </TableCell>

                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold"
                        }}
                        >
                        EPS
                        </TableCell>
                    </TableRow>
                    </TableHead>

                    {/* BODY */}
                    <TableBody>
                    {quarterlyTableData.map((row, idx) => (
                        <TableRow key={idx}>
                        
                        <TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>
                            {formatDate(row["Date"])}
                        </TableCell>

                        <TableCell sx={{ textAlign: "left" }}>
                            {row["normalizedBasicEps"]}
                        </TableCell>

                        </TableRow>
                    ))}
                    </TableBody>

                </Table>
                </TableContainer>
                </Box>

                <Box>
                <TableContainer sx={{ 
                    overflowX: 'auto', 
                    boxShadow: "none",
                    borderRadius: 2, 
                    width: 'fit-content',              
                    maxWidth: '100%',
                    mx: 2
                    }}>
                <Table stickyHeader size="small">

                    <TableHead>
                    <TableRow>
                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold",
                            borderRight: "1px solid #969696ff"
                        }}
                        >
                        Long-term stock chart
                        </TableCell>

                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold"
                        }}
                        >
                        Excluding Dividends
                        </TableCell>

                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold"
                        }}
                        >
                        Volume
                        </TableCell>

                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold"
                        }}
                        >
                        Dividends
                        </TableCell>

                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold"
                        }}
                        >
                        Reinvested Dividends
                        </TableCell>

                    </TableRow>
                    </TableHead>

                    {/* BODY */}
                    <TableBody>
                    {stockTableData.map((row, idx) => (
                        <TableRow key={idx}>
                        
                        <TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>
                            {formatDate(row["date"])}
                        </TableCell>

                        <TableCell sx={{ textAlign: "left" }}>
                            {row["ExcludingDividends"]}
                        </TableCell>

                        <TableCell sx={{ textAlign: "left" }}>
                            {row["volume"]}
                        </TableCell>

                        <TableCell sx={{ textAlign: "left" }}>
                            {row["dividends"]}
                        </TableCell>

                        <TableCell sx={{ textAlign: "left" }}>
                            {row["ReinvestedDividends"]}
                        </TableCell>

                        </TableRow>
                    ))}
                    </TableBody>

                </Table>
                </TableContainer>
                </Box>

                <Box>
                <TableContainer sx={{ 
                    overflowX: 'auto', 
                    boxShadow: "none",
                    borderRadius: 2, 
                    width: 'fit-content',              
                    maxWidth: '100%',
                    mx: 2
                    }}>
                <Table stickyHeader size="small">

                    <TableHead>
                    <TableRow>
                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold",
                            borderRight: "1px solid #969696ff"
                        }}
                        >
                        Two year stock chart
                        </TableCell>

                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold"
                        }}
                        >
                        Excluding Dividends
                        </TableCell>

                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold"
                        }}
                        >
                        Volume
                        </TableCell>

                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold"
                        }}
                        >
                        Dividends
                        </TableCell>

                        <TableCell
                        sx={{
                            backgroundColor: "#F9FAFB",
                            fontWeight: "bold"
                        }}
                        >
                        Reinvested Dividends
                        </TableCell>

                    </TableRow>
                    </TableHead>

                    {/* BODY */}
                    <TableBody>
                    {twoYearStock.map((row, idx) => (
                        <TableRow key={idx}>
                        
                        <TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>
                            {(row["date"])}
                        </TableCell>

                        <TableCell sx={{ textAlign: "left" }}>
                            {row["ExcludingDividends"]}
                        </TableCell>

                        <TableCell sx={{ textAlign: "left" }}>
                            {row["volume"]}
                        </TableCell>

                        <TableCell sx={{ textAlign: "left" }}>
                            {row["dividends"]}
                        </TableCell>

                        <TableCell sx={{ textAlign: "left" }}>
                            {row["ReinvestedDividends"]}
                        </TableCell>

                        </TableRow>
                    ))}
                    </TableBody>

                </Table>
                </TableContainer>
                </Box>


            </Box>
            </Box>
            ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "50vh", // full screen height
                    }}
                  >
                    <h3>Loading...</h3>
                    <CircularProgress />
                  </div>
            )}
        </>
    </>
  )}